import { useState, useCallback } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { ArrowUp, Sparkles } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { EDIT_PRESETS } from '@/inline-edit/constants/edit-presets'

type InlineEditPopoverProps = {
  anchorRect: DOMRect
  onSubmit: (instruction: string) => void
  onDismiss: () => void
}

function autoResize(el: HTMLTextAreaElement) {
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

export default function InlineEditPopover({ anchorRect, onSubmit, onDismiss }: InlineEditPopoverProps) {
  const [instruction, setInstruction] = useState('')

  const handleSubmit = useCallback(() => {
    const trimmed = instruction.trim()
    if (!trimmed) return
    onSubmit(trimmed)
  }, [instruction, onSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const submitDisabled = !instruction.trim()

  return (
    <Popover.Root
      open={true}
      onOpenChange={(open) => {
        if (!open) onDismiss()
      }}
    >
      <Popover.Anchor asChild>
        <div className="fixed" style={{ top: anchorRect.top, left: anchorRect.left, width: 0, height: anchorRect.height }} />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          sideOffset={8}
          align="start"
          data-inline-edit
          className="z-50 bg-white rounded-lg shadow-md border border-brand-neutral-200 p-2 w-[422px]"
          onOpenAutoFocus={(e) => {
            // Prevent auto-focus so the browser's native text selection is preserved.
            // The user can click the textarea when they want to type an instruction.
            e.preventDefault()
          }}
        >
          <div className="group flex items-center gap-3 border border-brand-neutral-300 focus-within:border-brand-400 rounded-[9px] min-h-[56px] pl-4 pr-2.5 py-2 bg-white transition-colors">
            <textarea
              rows={1}
              value={instruction}
              onChange={(e) => {
                setInstruction(e.target.value)
                autoResize(e.target)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Edit with a prompt"
              className="flex-1 text-base text-brand-neutral-900 placeholder:text-brand-neutral-600 border-0 outline-none focus:ring-0 resize-none overflow-hidden leading-6 tracking-[-0.32px] p-0"
            />
            <IconButton
              icon={<ArrowUp />}
              aria-label="Submit edit"
              size="lg"
              disabled={submitDisabled}
              onClick={handleSubmit}
              className={`shrink-0 transition-colors bg-true-gray-300 group-focus-within:bg-brand-500 ${submitDisabled ? 'opacity-50' : 'opacity-100'}`}
            />
          </div>
          <div className="flex flex-col gap-0.5 mt-0.5">
            {EDIT_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => onSubmit(preset.instruction)}
                className="flex items-center gap-2.5 w-full text-left p-2 h-10 text-sm text-brand-neutral-700 tracking-[-0.15px] leading-5 rounded-md hover:bg-brand-neutral-100 transition-colors"
              >
                <Sparkles className="size-4 shrink-0 text-brand-neutral-700" />
                {preset.label}
              </button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
